/* eslint-disable no-debugger */
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "@usas/ui-web/wrappers/ReactRouterDom";

import NavLinks from "../../NavLinks";

import useClubMeetsData from "../../../../state/clubMeets/UseClubMeetsData";
import useUsasClubFacilityLinksData from '../../../../state/clubFacilityLinks/UseUsasClubFacilityLinksData';

import { navigateToCrossUiRoute } from '@usas/ui-web/utils/FormatCrossUiRoute';
import useMeetSessionsData from '@usas/ui-web/state/meetSessions/UseMeetSessionsData';
import useBaseUsasClubData from "@usas/ui-web/state/clubBase/UseBaseUsasClubData";
import useEnvironmentVariableData from "@usas/ui-web/state/environmentVariable/UseEnvironmentVariableData";
import useGlobalRoutesData from '@usas/ui-web/state/security/UseGlobalRoutesData';
import useMeetData from '@usas/ui-web/state/meet/UseMeetNoContextData';

import Constants from "@usas/ui-web/utils/Constants";
import { formatDate } from "@usas/ui-web/utils/DateFunctions";

const UseClubMeetSanctionRequestV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { environmentVariableState, MEET_DIRECTOR_STAFF_TYPE_ID, MEET_REFEREE_STAFF_TYPE_ID, MEET_CONTACT_STAFF_TYPE_ID,
    ADMIN_OFFICIAL_STAFF_TYPE_ID, OFFICIAL_ORG_ROLE_ID, OFFICIALS_ROLES_ROLE_GROUP_ID, USAS_ORG_UNIT_ID } = useEnvironmentVariableData();
  const { baseUsasClubState } = useBaseUsasClubData();
  const { usasClubFacilityLinksState, getUsasClubFacilityLinks } = useUsasClubFacilityLinksData();
  const { clubMeetsState, postMeetSanctionRequest, putMeetSanctionRequest } = useClubMeetsData();
  const { meetState, getMeet } = useMeetData();
  const { getGlobalRoute } = useGlobalRoutesData();
  const [displayMeetInfoFormState, setDisplayMeetInfoFormState] = useState(true);
  const [displayMeetSessionsFormState, setDisplayMeetSessionsFormState] = useState(false);
  const [displayMeetSessionsDetailFormState, setDisplayMeetSessionsDetailFormState] = useState(false);
  const [displayStaffFormState, setDisplayStaffFormState] = useState(false);
  const [meetInfoState, setMeetInfoState] = useState({});
  const { meetSessionsState, setMeetSessionsState, resetModalState } = useMeetSessionsData();
  const [meetStaffState, setMeetStaffState] = useState({});
  const [facilityOptionsState, setFacilityOptionsState] = useState([{ id: Constants.DEFAULT_ID, name: '--' }]);
  const [state, setState] = useState({ error: '' });
  const [selectedMeetClassificationState, setSelectedMeetClassificationState] = useState({ id: Constants.DEFAULT_ID, name: '--' });

  const onInfoFormSubmit = (infoFormState) => {
    setState({ error: '' });
    setDisplayMeetInfoFormState(false);
    setDisplayMeetSessionsFormState(true);

    setMeetInfoState(infoFormState);
    setMeetSessionsState({
      ...meetSessionsState,
      formState: {
        ...meetSessionsState.formState,
        meetStartDate: meetInfoState.startDate ? formatDate(meetInfoState.startDate) : Constants.BLANK_DATE_STRING,
        meetEndDate: meetInfoState.endDate ? formatDate(meetInfoState.endDate) : Constants.BLANK_DATE_STRING
      }
    })
  }

  const onAddSession = () => {
    const prevHighestSessionOrderId = meetSessionsState.sortedMeetSessionArray?.length > 0 ? Math.max(...meetSessionsState.sortedMeetSessionArray?.map(o => o.sessionOrderId)) : 0;
    setMeetSessionsState({ ...meetSessionsState, formState: { ...meetSessionsState.formState, sessionOrderId: prevHighestSessionOrderId >= 0 ? prevHighestSessionOrderId + 1 : '' }, errorState: {} });
    setDisplayMeetSessionsFormState(false);
    setDisplayMeetSessionsDetailFormState(true);
  }

  const onEditSession = (e, session) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    setMeetSessionsState({ ...meetSessionsState, errorState: {}, formState: { ...meetSessionsState.formState, meetSessionId: session.meetSessionId } });
    setDisplayMeetSessionsFormState(false);
    setDisplayMeetSessionsDetailFormState(true);
  }

  const onDeleteSession = (e, meetSessionId) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    const meetSessionArrayCopy = JSON.parse(JSON.stringify(meetSessionsState.meetSessionArray));
    const selectedArrayIndex = meetSessionArrayCopy.findIndex(x => x.meetSessionId === meetSessionId);
    if (selectedArrayIndex > -1) {
      meetSessionArrayCopy.splice(selectedArrayIndex, 1);
      setMeetSessionsState({
        ...meetSessionsState,
        meetSessionArray: meetSessionArrayCopy,
        modalState: {
          displayPopUp: false,
          modalTitle: 'Delete Session?',
          meetSessionId: Constants.DEFAULT_ID,
          sessionOrderId: '',
          sessionTypeName: '',
          sessionDate: Constants.BLANK_DATE_STRING
        }
      })
    }
  }

  const onDeleteSessionClicked = (e, session) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setMeetSessionsState({
      ...meetSessionsState,
      modalState: {
        ...meetSessionsState.modalState,
        displayPopUp: true,
        meetSessionId: session.meetSessionId,
        sessionOrderId: session.sessionOrderId,
        sessionTypeName: session.sessionType?.typeName,
        sessionDate: session.sessionDate
      }
    })
  };

  const onModalCanceled = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    resetModalState();
  };

  const onSessionsFormSubmit = () => {
    let dateError = false;
    if (meetSessionsState.meetSessionArray.length > 0) {
      const meetStartDate = new Date(meetSessionsState.formState?.meetStartDate);
      const meetEndDate = new Date(meetSessionsState.formState?.meetEndDate);

      const numDays = Math.ceil((meetEndDate - meetStartDate) / (1000 * 60 * 60 * 24)) + 1;

      for (let i = 0; i < meetSessionsState.meetSessionArray.length; i++) {
        const sessionDate = new Date(meetSessionsState.meetSessionArray[i].sessionDate);
        if (sessionDate < meetStartDate || sessionDate > meetEndDate) {
          dateError = true;
        }
      }

      // Validate there is a session for every day from meetStartDate through meetEndDate
      let missingDays = [];
      for (let d = new Date(meetStartDate); d <= meetEndDate; d.setDate(d.getDate() + 1)) {
        const dayStr = d.toISOString().slice(0, 10);
        const hasSession = meetSessionsState.meetSessionArray.some(session => {
          const sessionDayStr = new Date(session.sessionDate).toISOString().slice(0, 10);
          return sessionDayStr === dayStr;
        });
        if (!hasSession) {
          missingDays.push(dayStr);
        }
      }

      if (missingDays.length > 0) {
        setMeetSessionsState({ ...meetSessionsState, errorState: { error: `Add at least one session for each day of the meet. Missing: ${missingDays.join(', ')}` } });
      } else if (dateError === true) {
        setMeetSessionsState({ ...meetSessionsState, errorState: { error: `All Session Dates must be between the Meet Start Date and Meet End Date (${meetSessionsState?.formState.meetStartDate || ''} - ${meetSessionsState?.formState.meetEndDate || ''}).` } });
      } else {
        setDisplayMeetSessionsFormState(false);
        setDisplayStaffFormState(true);
      }

      // if (numDays > meetSessionsState.meetSessionArray.length) {
      //   setMeetSessionsState({ ...meetSessionsState, errorState: { error: `Add at least one session for each day of the meet - (${numDays} days).` } });
      // } else if (dateError === true) {
      //   setMeetSessionsState({ ...meetSessionsState, errorState: { error: `All Session Dates must be between the Meet Start Date and Meet End Date (${meetSessionsState?.formState.meetStartDate || ''} - ${meetSessionsState?.formState.meetEndDate || ''}).` } });
      // } else {
      //   setDisplayMeetSessionsFormState(false);
      //   setDisplayStaffFormState(true);
      // }

    }
  };

  const onStaffFormSubmit = (staffFormState) => {
    setMeetStaffState(staffFormState);

    var meetObj = null;
    var descOfMedSup = meetInfoState.descriptionOfMedicalSupervision ? meetInfoState.descriptionOfMedicalSupervision : '';
    var requestSig = meetInfoState.attestationSignature ? meetInfoState.attestationSignature : '';

    // Check if meetInfoState contains meetId property
    if (meetInfoState && Object.prototype.hasOwnProperty.call(meetInfoState, 'meetId')) {
      meetObj = createMeetSanctionRequestForPut(meetState, meetInfoState, staffFormState);
      meetObj.meetId = meetInfoState.meetId;
      putMeetSanctionRequest(meetInfoState.meetId, meetInfoState.meetSanctionRequestId, descOfMedSup, requestSig, meetObj);
    } else {
      meetObj = createMeetSanctionRequest(baseUsasClubState.objData.orgUnitId, baseUsasClubState.objData.orgUnit.orgUnitParentChild[0].parentOrgUnitId, meetInfoState, staffFormState);
      postMeetSanctionRequest(descOfMedSup, requestSig, meetObj);
    }
  };

  const onCancelClicked = () => {
    navigate(NavLinks.CLUB_MEETS);
  };

  const onBackToMeetInfoClicked = () => {
    let dateError = false;
    if (meetSessionsState.meetSessionArray.length > 0) {
      const meetStartDate = new Date(meetSessionsState.formState.meetStartDate);
      const meetEndDate = new Date(meetSessionsState.formState.meetEndDate);

      for (let i = 0; i < meetSessionsState.meetSessionArray.length; i++) {
        const sessionDate = new Date(meetSessionsState.meetSessionArray[i].sessionDate);
        if (sessionDate < meetStartDate || sessionDate > meetEndDate) {
          dateError = true;
        }
      }

      if (dateError === true) {
        setMeetSessionsState({ ...meetSessionsState, errorState: { error: `All Session Dates must be between the Meet Start Date and Meet End Date (${meetSessionsState?.formState?.meetStartDate || ''} - ${meetSessionsState?.formState?.meetEndDate || ''}).` } });
      }
      else {
        setDisplayMeetSessionsFormState(false);
        setDisplayMeetInfoFormState(true);
      }
    }
    else {
      setMeetSessionsState({ ...meetSessionsState, errorState: { error: 'At least one Session is required.' } });
    }
  };

  const onBackToMeetSessionsClicked = (staffFormState) => {
    setDisplayStaffFormState(false);
    setDisplayMeetSessionsFormState(true);
    setMeetStaffState(staffFormState);
  };

  const onBackToMeetSessionsFromDetail = () => {
    setDisplayMeetSessionsDetailFormState(false);
    setDisplayMeetSessionsFormState(true);
  };

  // function getMeetClassificationIdByName(meetClassificationArray, meetClassification, meetType) {
  //   const subArray = meetClassificationArray.find(x => x.name === meetClassification);
  //   const match = subArray?.children?.find(
  //     (item) => item.name === meetType
  //   );
  //   return match ? match.id : null;
  // }

  useEffect(() => {
    if (location.state?.meet) {
      const meetInfo = location.state.meet;

      getMeet(meetInfo.meetId);
      setMeetInfoState(meetInfo);
    } 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (meetState.isObjLoaded === true && meetState.isObjLoading === false) {
      const meetClassificationId = meetState.objData.meetClassification.meetClassificationId;
      const meetType = meetState.objData.meetClassification.typeName; 
      
      setSelectedMeetClassificationState({ id: meetClassificationId, name: meetType });

      const sessionArray = [];
      for (let i = 0; i < meetState.objData.meetSession.length; i++) {
        const session = meetState.objData.meetSession[i];

        sessionArray.push({ 
          meetSessionId: session.meetSessionId, 
          sessionOrderId: session.sessionOrderId, 
          sessionTypeId: session.sessionTypeId,
          sessionType: { typeId: session.sessionTypeId, typeName: session.sessionType.typeName }, 
          sessionDate: session.sessionDate });
      }

      setMeetSessionsState({ ...meetSessionsState, meetSessionArray: sessionArray, sortedMeetSessionArray: sessionArray });

      setDisplayMeetInfoFormState(true);
      setDisplayMeetSessionsFormState(false);
    } 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetState]);

  useEffect(() => {
    if (baseUsasClubState.isObjLoading === false && baseUsasClubState.isObjLoaded === true
      && usasClubFacilityLinksState.isArrayLoaded === false && usasClubFacilityLinksState.isArrayLoading === false) {
      getUsasClubFacilityLinks(baseUsasClubState.objData.usasClubId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUsasClubState, usasClubFacilityLinksState]);

  useEffect(() => {
    if (baseUsasClubState.isObjLoading === false && baseUsasClubState.isObjLoaded === true
      && usasClubFacilityLinksState.isArrayLoading === false && usasClubFacilityLinksState.isArrayLoaded === true) {
      setFacilityOptionsState(getFacilityAsOptions(usasClubFacilityLinksState.arrayData || []));
    }
  }, [baseUsasClubState, usasClubFacilityLinksState]);

  useEffect(() => {
    if (meetSessionsState.meetSessionArray?.length > 0) {
      //Sort grid data to display in ascending order
      const meetSessionArrayCopy = JSON.parse(JSON.stringify(meetSessionsState.meetSessionArray));
      meetSessionArrayCopy.sort(function (a, b) { return a.sessionOrderId - b.sessionOrderId });
      setMeetSessionsState({
        ...meetSessionsState,
        sortedMeetSessionArray: meetSessionArrayCopy,
        formState: {
          ...meetSessionsState.formState,
          meetStartDate: meetInfoState.startDate ? formatDate(meetInfoState.startDate) : Constants.BLANK_DATE_STRING,
          meetEndDate: meetInfoState.endDate ? formatDate(meetInfoState.endDate) : Constants.BLANK_DATE_STRING
        }
      });
    }
    else {
      setMeetSessionsState({
        ...meetSessionsState,
        sortedMeetSessionArray: [],
        formState: {
          ...meetSessionsState.formState,
          meetStartDate: meetInfoState.startDate ? formatDate(meetInfoState.startDate) : Constants.BLANK_DATE_STRING,
          meetEndDate: meetInfoState.endDate ? formatDate(meetInfoState.endDate) : Constants.BLANK_DATE_STRING
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetSessionsState.meetSessionArray, meetInfoState.startDate, meetInfoState.endDate]);

  useEffect(() => {
    if (clubMeetsState.isSaved === true) {
      navigate(NavLinks.CLUB_MEETS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubMeetsState.isSaved]);

  useEffect(() => {
    if (clubMeetsState.isObjLoaded === true && clubMeetsState.objData.sanctionOrgUnitId === USAS_ORG_UNIT_ID) {
      if (clubMeetsState.objData.status === "Pending"
        // && formState.hasCartItem === false
      ) {
        const paymentsRoute = getGlobalRoute('PAYMENTS_SHOPPING_CART');

        if (paymentsRoute && paymentsRoute.route) {
          navigateToCrossUiRoute(paymentsRoute.uIProjectName, paymentsRoute.route, navigate);
        }
      } else {
        navigate(NavLinks.CLUB_MEETS);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubMeetsState]);

  function getFacilityAsOptions(facilityArray) {
    const facilityOptions = [{ id: Constants.DEFAULT_ID, name: '--' }];
    if (facilityArray) {
      for (const facility of facilityArray) {
        if (facility.facility && facility.facility.hostMeet === true) {
          facilityOptions.push({ id: facility.facility.facilityId, name: facility.facility.facilityName + (facility.facility.stateCode ? ` (${facility.facility.stateCode})` : '')});
        }
      }
    }
    return facilityOptions;
  }

  function createMeetSanctionRequestForPut(meetState, meetInfo, staffFormState) {
    const currentSessionArray = meetSessionsState.sortedMeetSessionArray;
    const oldSessionArray = meetState.objData.meetSession;

    for (let i = 0; i < oldSessionArray.length; i++) {
      const oldSession = oldSessionArray[i];
      const newSession = currentSessionArray.find(session => session.meetSessionId === oldSession.meetSessionId);

      if (newSession) {
        oldSessionArray[i].sessionTypeId = newSession?.sessionTypeId || oldSession.sessionTypeId;
        oldSessionArray[i].sessionDate = newSession?.sessionDate || oldSession.sessionDate;
      } else {
        oldSessionArray.splice(i, 1);
        i--; 
      }
    }

    for (let i = 0; i < currentSessionArray.length; i++) {
      const newSession = currentSessionArray[i];
      const oldSession = oldSessionArray.find(session => session.meetSessionId === newSession.meetSessionId);

      const sessionObj = {};

      if (!oldSession) {
        sessionObj.sessionOrderId = newSession.sessionOrderId;
        sessionObj.sessionDate = newSession.sessionDate;
        sessionObj.sessionTypeId = newSession.sessionTypeId;
        sessionObj.meetId = meetState.objData.meetId;
        sessionObj.sessionType = null;

        oldSessionArray.push(sessionObj);
      }
    }

    return {
      meetId: meetState.objData.meetId,
      meetClassificationId: meetInfo.meetClassification[0].id, 
      meetName: meetInfo.autogeneratedMeetYear + ' ' + meetInfo.autogeneratedMeetLsc + ' ' + meetInfo.meetName,
      startDate: meetInfo.startDate,
      endDate: meetInfo.endDate,
      hostOrgUnitId: meetState.objData.hostOrgUnitId,
      facilityId: meetInfo.facilityId,
      meetAbbreviation: '',
      meetSoftwareId: meetInfo.meetSoftwareId,
      meetAnnouncementUrl: meetInfo.meetAnnouncementUrl || '',
      meetSanctionNumber: meetState.objData.meetSanctionNumber || '',
      meetClassification: meetState.objData.meetClassification,
      status: meetState.objData.status, 
      workflowId: meetState.objData.workflowId,
      isUploadLocked: meetState.objData.isUploadLocked,
      meetResultsUploaded: meetState.objData.meetResultsUploaded,
      meetResultsDocUrl: meetState.objData.meetResultsDocUrl || '',
      isInWaterStart: meetInfo.isInWaterStart,
      sanctionOrgUnitId: meetInfo.isUsasSanctionMeet ? USAS_ORG_UNIT_ID : meetState.objData.sanctionOrgUnitId,
      meetFormatId: meetInfo.meetFormatId,
      meetCourse: meetInfo.meetCourse.map(
        (course, x) => { return { courseId: course.id, meetId: meetState.objData.meetId }; }),
      isUsasMeet: true,
      isFinaMeet: false,
      isInternationalSwimLeagueMeet: false,
      isNcaaMeet: false,
      meetSession: oldSessionArray, 
      personMeetStaff: [
        {
          personMeetStaffId: meetState.objData.personMeetStaff[0].personMeetStaffId, 
          meetId: meetState.objData.meetId,
          personId: staffFormState.meetDirectorPersonId,  
          personMeetStaffType: [
            {
              personMeetStaffTypeId: meetState.objData.personMeetStaff[0].personMeetStaffType[0].personMeetStaffTypeId,
              personMeetStaffId: meetState.objData.personMeetStaff[0].personMeetStaffType[0].personMeetStaffId,
              meetStaffTypeId: MEET_DIRECTOR_STAFF_TYPE_ID,
              meetStaffType: null
            }
          ]
        },
        {
          personMeetStaffId: meetState.objData.personMeetStaff[1].personMeetStaffId, 
          meetId: meetState.objData.meetId,
          personId: staffFormState.meetRefereePersonId, 
          personMeetStaffType: [
            {
              personMeetStaffTypeId: meetState.objData.personMeetStaff[1].personMeetStaffType[0].personMeetStaffTypeId,
              personMeetStaffId: meetState.objData.personMeetStaff[1].personMeetStaffType[0].personMeetStaffId,
              meetStaffTypeId: MEET_REFEREE_STAFF_TYPE_ID,
              meetStaffType: null
            }
          ]
        },
        {
          personMeetStaffId: meetState.objData.personMeetStaff[2].personMeetStaffId, 
          meetId: meetState.objData.meetId,
          personId: staffFormState.meetContactPersonId, 
          personMeetStaffType: [
            {
              personMeetStaffTypeId: meetState.objData.personMeetStaff[2].personMeetStaffType[0].personMeetStaffTypeId,
              personMeetStaffId: meetState.objData.personMeetStaff[2].personMeetStaffType[0].personMeetStaffId,
              meetStaffTypeId: MEET_CONTACT_STAFF_TYPE_ID,
              meetStaffType: null
            }
          ]
        },
        {
          personMeetStaffId: meetState.objData.personMeetStaff[3].personMeetStaffId, 
          meetId: meetState.objData.meetId,
          personId: staffFormState.meetAdminOfficialPersonId, 
          personMeetStaffType: [
            {
              personMeetStaffTypeId: meetState.objData.personMeetStaff[3].personMeetStaffType[0].personMeetStaffTypeId,
              personMeetStaffId: meetState.objData.personMeetStaff[3].personMeetStaffType[0].personMeetStaffId,
              meetStaffTypeId: ADMIN_OFFICIAL_STAFF_TYPE_ID,
              meetStaffType: null
            }
          ]
        }
      ]
    };
  }

  function createMeetSanctionRequest(orgUnitId, santionOrgUnitId, meetInfo, meetStaff) {
    let meetSessionArrayCopy = JSON.parse(JSON.stringify(meetSessionsState.meetSessionArray));
    for (let i = 0; i < meetSessionArrayCopy.length; i++) {
      delete meetSessionArrayCopy[i].meetSessionId;
      meetSessionArrayCopy[i].sessionType = undefined;
    }

    return {
      meetClassificationId: meetInfo.meetClassification[0].id,
      // meetName: meetInfo.autogeneratedMeetYear + ' ' + meetInfo.autogeneratedMeetLsc + ' ' + meetInfo.meetName,
      meetName: meetInfo.autogeneratedMeetYear + ' ' + baseUsasClubState.objData?.orgUnit?.parentOrgUnitCode + ' ' + meetInfo.meetName,
      startDate: meetInfo.startDate,
      endDate: meetInfo.endDate,
      hostOrgUnitId: orgUnitId,
      isUsasMeet: true,
      isFinaMeet: false,
      isInternationalSwimLeagueMeet: false,
      isNcaaMeet: false,
      meetCourse: meetInfo.meetCourse.map(course => { return { courseId: course.id }; }),
      facilityId: meetInfo.facilityId,

      isInWaterStart: meetInfo.isInWaterStart,
      depthRequirementMet: meetInfo.depthRequirementMet,
      meetFormatId: meetInfo.meetFormatId,

      meetAbbreviation: '',
      meetSoftwareId: meetInfo.meetSoftwareId,
      meetAnnouncementUrl: meetInfo.meetAnnouncementUrl,

      sanctionOrgUnitId: meetInfo.isUsasSanctionMeet ? USAS_ORG_UNIT_ID : santionOrgUnitId,

      status: Constants.WORKFLOW_STATUS_PENDING,
      meetSession: meetSessionArrayCopy,

      personMeetStaff: [
        {
          personId: meetStaff.meetDirectorPersonId,
          personMeetStaffType: [
            {
              meetStaffTypeId: MEET_DIRECTOR_STAFF_TYPE_ID,
              meetStaffType: null
            }
          ]
        },
        {
          personId: meetStaff.meetRefereePersonId,
          personMeetStaffType: [
            {
              meetStaffTypeId: MEET_REFEREE_STAFF_TYPE_ID,
              meetStaffType: null
            }
          ]
        },
        {
          personId: meetStaff.meetContactPersonId,
          personMeetStaffType: [
            {
              meetStaffTypeId: MEET_CONTACT_STAFF_TYPE_ID,
              meetStaffType: null
            }
          ]
        },
        {
          personId: meetStaff.meetAdminOfficialPersonId,
          personMeetStaffType: [
            {
              meetStaffTypeId: ADMIN_OFFICIAL_STAFF_TYPE_ID,
              meetStaffType: null
            }
          ]
        }
      ]
    };
  }

  return {
    isSaving: clubMeetsState.isSaving,
    isLoading: environmentVariableState.isLoading || meetState.isObjLoading || baseUsasClubState.isObjLoading || usasClubFacilityLinksState.isArrayLoading,
    meetState,
    meetInfoState,
    meetSessionsState,
    meetStaffState,
    displayMeetInfoFormState,
    displayMeetSessionsFormState,
    displayMeetSessionsDetailFormState,
    displayStaffFormState,
    facilityOptionsState,
    orgUnitId: baseUsasClubState.objData.orgUnitId || '',
    onInfoFormSubmit,
    onSessionsFormSubmit,
    onStaffFormSubmit,
    onAddSession,
    onEditSession,
    onDeleteSession,
    onDeleteSessionClicked,
    onModalCanceled,
    onBackToMeetInfoClicked,
    onBackToMeetSessionsClicked,
    onBackToMeetSessionsFromDetail,
    onCancelClicked,
    state,
    setState,
    OFFICIAL_ORG_ROLE_ID,
    OFFICIALS_ROLES_ROLE_GROUP_ID,
    selectedMeetClassificationState,
    hasUsasSanctionAccess: baseUsasClubState.objData?.hasUsasSanctionAccess || false
  };
};

export default UseClubMeetSanctionRequestV2;

